// Modul „Skladové zásoby": hodnota skladu (CZ) v prodejních cenách bez DPH,
// po hlavních kategoriích produktů. Ikona seznamu v dlaždici otevře produkty
// kategorie seřazené podle hodnoty skladu. Data živě z Shoptet exportu.

import { CategoryCard } from '@/components/zasoby/CategoryCard';
import { ProductsModal } from '@/components/zasoby/ProductsModal';
import { Sparkline } from '@/components/zasoby/Sparkline';
import { type StockCategory, type StockValueResponse, fmtCzk } from '@/components/zasoby/types';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime, fmtNum } from '@/lib/format';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

const topLevel = (path: string) => path.split('>')[0]?.trim() ?? path;

function SummaryCard({
  label,
  value,
  badge,
  foot,
  children,
}: {
  label: string;
  value: string;
  badge: string;
  foot: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="bg-surface rounded-xl border border-border shadow-card p-5 flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm text-text-secondary">{label}</div>
        <span className="text-xs font-semibold rounded-md bg-bg px-2 py-1 text-text-secondary">
          {badge}
        </span>
      </div>
      <div className="mt-1 text-2xl font-bold text-text">{value}</div>
      {children}
      <div className="mt-auto pt-3 border-t border-border-light text-xs text-text-muted">
        {foot}
      </div>
    </div>
  );
}

export function SkladoveZasobyPage() {
  const [data, setData] = useState<StockValueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState<string | null>(null);
  const [open, setOpen] = useState<StockCategory | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<StockValueResponse>('/api/skladove-zasoby')
      .then(setData)
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.describe() : 'Skladové zásoby se nenačetly'),
      )
      .finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => {
    if (!data) return [];
    const sums = new Map<string, number>();
    for (const c of data.categories) {
      const g = topLevel(c.path);
      sums.set(g, (sums.get(g) ?? 0) + c.valueNet);
    }
    return [...sums.entries()].sort((a, b) => b[1] - a[1]).map(([g]) => g);
  }, [data]);

  const visible = useMemo(
    () => (data?.categories ?? []).filter((c) => !group || topLevel(c.path) === group),
    [data, group],
  );

  const modalProducts = useMemo(
    () => (open && data ? data.products.filter((p) => p.category === open.path) : []),
    [data, open],
  );

  const t = data?.totals;
  const margin = t ? t.valueNet - t.purchase : 0;

  return (
    <>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-text">Skladové zásoby</h1>
        <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
          Hodnota skladu e-shopu v-mart.cz v prodejních cenách bez DPH, po hlavních kategoriích
          produktů. Každý produkt se počítá jen do své hlavní kategorie.
        </p>
      </div>

      {loading && <div className="text-sm text-text-secondary">Načítám feed ze Shoptetu…</div>}
      {error && (
        <div className="rounded-xl border border-danger/30 bg-danger-light px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      {data && t && (
        <>
          <div className="grid gap-4 md:grid-cols-3 mb-6">
            <SummaryCard
              label="Hodnota v prodeji bez DPH"
              value={fmtCzk(t.valueNet)}
              badge="Prodej"
              foot={`${fmtNum(t.qty)} ks · data z ${fmtDateTime(data.generatedAt)}`}
            >
              <Sparkline points={data.totalHistory} className="mt-2 h-10 w-full" />
            </SummaryCard>
            <SummaryCard
              label="Hodnota v nákupu"
              value={fmtCzk(t.purchase)}
              badge="Nákup"
              foot="Nákupní ceny ze Shoptetu"
            />
            <SummaryCard
              label="Potenciální hrubý zisk"
              value={fmtCzk(margin)}
              badge="Marže"
              foot={`Prodej bez DPH − nákup${
                t.valueNet > 0 ? ` · ${Math.round((margin / t.valueNet) * 100)} % z prodeje` : ''
              }`}
            />
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            {[null, ...groups].map((g) => (
              <button
                key={g ?? '__all'}
                type="button"
                onClick={() => setGroup(g)}
                className={`px-3 py-1.5 text-sm font-semibold rounded-full border cursor-pointer ${
                  group === g
                    ? 'bg-primary text-white border-primary'
                    : 'bg-surface text-text-secondary border-border hover:text-text'
                }`}
              >
                {g ?? 'Vše'}
              </button>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {visible.map((c) => (
              <CategoryCard
                key={c.path}
                category={c}
                share={t.valueNet > 0 ? (c.valueNet / t.valueNet) * 100 : 0}
                onOpen={() => setOpen(c)}
              />
            ))}
          </div>
        </>
      )}

      {open && (
        <ProductsModal category={open} products={modalProducts} onClose={() => setOpen(null)} />
      )}
    </>
  );
}
